/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable react/no-array-index-key */
import React, { useRef, useEffect } from "react";
import {
  SmartToy as SmartToyIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  ChatBubbleOutline as ChatIcon,
  LocalOffer as PricingIcon,
  Explore as TourIcon,
  Psychology as HireIcon,
  Language as StatusIcon,
  Remove as RemoveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  North as SendUpIcon,
  ThumbUpOffAlt as ThumbUpIcon,
  ThumbDownOffAlt as ThumbDownIcon,
  RadioButtonUnchecked as RadioUncheckedIcon,
  RadioButtonChecked as RadioCheckedIcon,
  Logout as LogoutIcon,
  MicNone as MicIcon,
  Mic as MicActiveIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import EmojiPicker from "src/components/shared/atoms/Emoji";
import KuikwitLogoOnly from "src/assets/images/auth/kuikwit_logo.png";
import { ChatbotConfig } from "./types";

interface WidgetPreviewProps {
  config: ChatbotConfig;
  isStandalone?: boolean;
  onSendMessage?: (message: string, file?: File) => void;
  isTyping?: boolean;
  externalMessages?: Message[];
  botError?: string | null;
  status?: "active" | "handed_off" | "completed";
  isSending?: boolean;
  onCloseChat?: () => void;
  onSubmitFeedback?: (rating: "up" | "down", ease: string) => void;
  conversationId?: string | null;
  closedByCustomer?: boolean;
  initialThumbs?: string | null;
  initialFeedback?: string | null;
  onTranscribe?: (
    audio: Blob,
  ) => Promise<{ success: boolean; text?: string; error?: string }>;
  onResetChat?: () => void;
  isResetLoading?: boolean;
}

interface Message {
  id: string;
  text: string;
  sender: "bot" | "user";
  timestamp: Date;
  fileUrl?: string;
  fileType?: string;
  isSystem?: boolean;
  isSending?: boolean;
}

const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  config,
  isStandalone,
  onSendMessage,
  isTyping,
  externalMessages,
  botError,
  isSending = false,
  onCloseChat,
  onSubmitFeedback,
  conversationId,
  closedByCustomer,
  initialThumbs,
  initialFeedback,
  status,
  onTranscribe,
  onResetChat,
  isResetLoading = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [currentView, setCurrentView] = React.useState<
    "home" | "chat" | "feedback"
  >("home");
  const [showCloseConfirmation, setShowCloseConfirmation] =
    React.useState(false);
  const [feedbackRating, setFeedbackRating] = React.useState<
    "up" | "down" | null
  >((initialThumbs as "up" | "down") || null);
  const [feedbackEase, setFeedbackEase] = React.useState<string | null>(
    initialFeedback || null,
  );
  const [localMessages, setLocalMessages] = React.useState<Message[]>([
    {
      id: "1",
      text:
        config.behavior?.welcomeMessage || "Hello! How can I help you today?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = React.useState("");
  const [isRecording, setIsRecording] = React.useState(false);
  const [isTranscribing, setIsTranscribing] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Use external messages if provided, otherwise use local state
  const messages = externalMessages || localMessages;

  // If standalone, we are always open and controlled by the iframe container
  const effectiveIsOpen = isStandalone ? true : isOpen;

  const scrollRef = useRef<HTMLDivElement>(null);
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping, effectiveIsOpen]);

  // Handle redirect to feedback if chat is closed by customer but no feedback given
  useEffect(() => {
    if (currentView === "chat" && closedByCustomer && !initialThumbs) {
      setCurrentView("feedback");
    }
  }, [currentView, closedByCustomer, initialThumbs]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (config.behavior?.autoOpen && !isStandalone && !hasAutoOpened.current) {
      timer = setTimeout(() => {
        setIsOpen(true);
        hasAutoOpened.current = true;
      }, config.behavior.autoOpenDelay || 3000);
      return () => clearTimeout(timer);
    }

    // Reset if autoOpen is disabled, to allow it to trigger again if re-enabled
    if (!config.behavior?.autoOpen) {
      hasAutoOpened.current = false;
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [config.behavior?.autoOpen, config.behavior?.autoOpenDelay, isStandalone]);

  const handleToggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        const audioFile = new File([audioBlob], "audio.webm", {
          type: "audio/webm",
        });

        if (onTranscribe) {
          setIsTranscribing(true);
          const result = await onTranscribe(audioFile);
          setIsTranscribing(false);

          if (result.success && result.text) {
            setInputValue((prev) => {
              const spaced = prev.trim() ? `${prev.trim()} ` : "";
              return spaced + result.text;
            });
          } else if (result.error) {
            console.error("Transcription error:", result.error);
          }
        }

        // Stop all tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      alert(
        "Could not access microphone. Please ensure you have given permission.",
      );
    }
  };

  const handleSendMessage = (file?: File) => {
    if (!inputValue.trim() && !file) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
      fileUrl: file ? URL.createObjectURL(file) : undefined,
      fileType: file ? file.type : undefined,
    };

    if (onSendMessage) {
      onSendMessage(inputValue, file);
    } else {
      setLocalMessages((prev) => [...prev, newMessage]);
      // Simulate a bot response for preview/settings
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "I've received your message. This is a preview of the chatbot functionality.",
          sender: "bot",
          timestamp: new Date(),
        };
        setLocalMessages((prev) => [...prev, botResponse]);
      }, 1000);
    }

    setInputValue("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleSendMessage(file);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const handleClose = () => {
    if (isStandalone) {
      window.parent.postMessage({ type: "KUIKWIT_CHATBOT_CLOSE" }, "*");
    } else {
      setIsOpen(false);
    }
  };

  if (!config?.appearance || config.enabled === false) return null;

  const items = (config.links || []).map((link, idx) => ({
    icon: link.iconUrl ? (
      <Box
        component="img"
        src={link.iconUrl}
        sx={{ width: 24, height: 24, objectFit: "contain" }}
      />
    ) : (
      [
        <PricingIcon sx={{ color: "#fbc02d" }} />,
        <TourIcon sx={{ color: "#0288d1" }} />,
        <HireIcon sx={{ color: "#455a64" }} />,
        <StatusIcon sx={{ color: "#7b1fa2" }} />,
      ][idx % 4]
    ),
    text: link.text,
    url: link.url,
  }));

  return (
    <Box
      sx={{
        height: isStandalone ? "100%" : "600px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        justifyContent: "flex-end",
        alignItems: isStandalone ? "stretch" : "flex-end",
        p: isStandalone ? 0 : 1,
        pointerEvents: "none", // Allow clicks through the container to the settings fields
        "& > *": { pointerEvents: "auto" }, // Re-enable clicks for actual widget elements
      }}
    >
      {/* Main Chat Window */}
      {effectiveIsOpen && (
        <Box
          sx={{
            width: isStandalone ? "100%" : { xs: "100%", sm: 350 },
            height: isStandalone ? "100%" : 500,
            maxHeight: isStandalone ? "100%" : "85vh",
            bgcolor:
              currentView === "home"
                ? "transparent"
                : config.appearance.backgroundColor ||
                  (config.appearance.darkMode ? "#1a1a1a" : "#ffffff"),
            background:
              currentView === "home"
                ? `linear-gradient(180deg, ${config.appearance.primaryColor} 0%, #ffffff 100%)`
                : "none",
            color:
              currentView === "home"
                ? "#ffffff"
                : config.appearance.darkMode
                ? "#ffffff"
                : "#000000",
            boxShadow: isStandalone ? "none" : 10,
            borderRadius: isStandalone
              ? 0
              : `${config.appearance.borderRadius || 12}px`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            mb: isStandalone ? 0 : 2,
            flex: isStandalone ? 1 : "initial",
            border: isStandalone
              ? "none"
              : config.appearance.darkMode
              ? "1px solid #333"
              : "1px solid #eee",
            zIndex: 10,
          }}
        >
          {/* Widget Header - Transparent for Home view since background is on parent */}
          <Box
            sx={{
              p: 2.5,
              bgcolor: config.appearance.primaryColor,
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              zIndex: 2,
              position: "relative",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {currentView === "home" ? (
                <Typography
                  variant="h4"
                  fontWeight="700"
                  sx={{ letterSpacing: "-0.5px" }}
                >
                  Chat with us!
                </Typography>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentView("home")}
                    sx={{
                      color: "inherit",
                      p: 0,
                      mr: 0.5,
                      "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {config.appearance.avatarUrl ? (
                      <Box
                        component="img"
                        src={config.appearance.avatarUrl}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          border: "2px solid rgba(255,255,255,0.8)",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: "rgba(255,255,255,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SmartToyIcon sx={{ fontSize: 24 }} />
                      </Box>
                    )}
                    <Typography variant="body1" fontWeight="600">
                      {status === "handed_off"
                        ? config.behavior.agentTakeoverName || "Live Support"
                        : config.behavior.botTakeoverName ||
                          config.name ||
                          "Chat Support"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {currentView === "chat" &&
                conversationId &&
                messages.some(
                  (m) =>
                    m.sender === "bot" && m.id !== "welcome" && m.id !== "1",
                ) && (
                  <Tooltip title="Close the chat" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setShowCloseConfirmation(true)}
                      sx={{
                        color: "#ffffff",
                        opacity: 0.8,
                        "&:hover": {
                          opacity: 1,
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title="minimize window" arrow>
                <IconButton
                  onClick={handleClose}
                  sx={{
                    color: "#ffffff",
                    opacity: 0.8,
                    "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.1)" },
                    alignSelf: "center",
                  }}
                >
                  <RemoveIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
              bgcolor:
                currentView === "home"
                  ? "transparent"
                  : config.appearance.backgroundColor ||
                    (config.appearance.darkMode ? "#121212" : "#f8f9fa"),
              zIndex: 3,
            }}
          >
            {currentView === "home" ? (
              /* HOME VIEW */
              <Box
                sx={{
                  flex: 1,
                  px: 2,
                  pb: 2,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "rgba(255,255,255,0.2)",
                    borderRadius: "4px",
                  },
                }}
              >
                {/* Text Support Card */}
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    overflow: "hidden",
                    bgcolor: config.appearance.darkMode ? "#1e1e1e" : "#fff",
                    flexShrink: 0,
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      padding: "12px 16px !important",
                      "&:last-child": { pb: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "success.main",
                          width: 40,
                          height: 40,
                          boxShadow: "0 4px 10px rgba(76, 175, 80, 0.3)",
                        }}
                      >
                        <SmartToyIcon />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="textSecondary"
                        >
                          Text Support
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                          Engage customers easily with Kuikwit tools. Explore
                          more.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setCurrentView("chat")}
                      sx={{
                        bgcolor: "#1e1e1e",
                        color: "#fff",
                        borderRadius: 3,
                        p: "5px",
                        textTransform: "none",
                        fontWeight: "700",
                        fontSize: "0.95rem",
                        display: "flex",
                        gap: 1,
                        "&:hover": {
                          bgcolor: "#333",
                          transform: "scale(1.01)",
                          "& .send-icon": {
                            transform: "translateX(3px)",
                          },
                        },
                        "& .send-icon": {
                          transition:
                            "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        },
                      }}
                    >
                      Let's chat{" "}
                      <ArrowForwardIcon
                        className="send-icon"
                        sx={{ fontSize: 18 }}
                      />
                    </Button>
                  </CardContent>
                </Card>

                <List
                  sx={{
                    bgcolor: "transparent",
                    py: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: items.length > 3 ? "230px" : "auto",
                    overflowY: items.length > 3 ? "auto" : "visible",
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "rgba(0,0,0,0.1)",
                      borderRadius: "4px",
                    },
                    flexShrink: 0,
                  }}
                >
                  {items.map((item, idx) => (
                    <ListItem
                      key={idx}
                      disablePadding
                      sx={{
                        bgcolor: config.appearance.darkMode
                          ? "#1e1e1e"
                          : "#fff",
                        flexShrink: 0, // ⭐ prevents squeezing
                        borderRadius: 3,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        border: "1px solid rgba(0,0,0,0.05)",
                        overflow: "hidden",
                        "&:hover": {
                          "& .arrow-container": {
                            bgcolor: config.appearance.primaryColor,
                            transform: "translateX(5px)",
                          },
                          "& .arrow-icon": {
                            color: "#fff",
                          },
                        },
                      }}
                      secondaryAction={
                        <Box
                          className="arrow-container"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: config.appearance.darkMode
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          <ArrowForwardIcon
                            className="arrow-icon"
                            sx={{
                              fontSize: 14,
                              color: "text.secondary",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </Box>
                      }
                    >
                      <ListItemButton
                        onClick={() => {
                          if (item.url && item.url !== "#") {
                            window.open(
                              item.url,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }
                        }}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: "600",
                            color: config.appearance.darkMode
                              ? "#ffffff"
                              : "#1a1a1a",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : currentView === "feedback" ? (
              /* FEEDBACK VIEW */
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  bgcolor: config.appearance.darkMode ? "#121212" : "#fff",
                }}
              >
                <Box sx={{ textAlign: "center", mb: 1 }}>
                  <Typography variant="body1" fontWeight="500">
                    Thanks! Your feedback means a lot and helps me grow.
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{ mb: 1.5, display: "flex", gap: 0.5 }}
                  >
                    How would you rate agent?{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <IconButton
                      onClick={() => setFeedbackRating("up")}
                      sx={{
                        color:
                          feedbackRating === "up" ? "success.main" : "inherit",
                        bgcolor:
                          feedbackRating === "up"
                            ? "success.light"
                            : "transparent",
                        "&:hover": { bgcolor: "success.light", opacity: 0.8 },
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <ThumbUpIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => setFeedbackRating("down")}
                      sx={{
                        color:
                          feedbackRating === "down" ? "error.main" : "inherit",
                        bgcolor:
                          feedbackRating === "down"
                            ? "error.light"
                            : "transparent",
                        "&:hover": { bgcolor: "error.light", opacity: 0.8 },
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <ThumbDownIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
                    How easy was it to solve your case today?
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {[
                      "Very easy",
                      "Easy",
                      "Neutral",
                      "Difficult",
                      "Very difficult",
                    ].map((option) => (
                      <Box
                        key={option}
                        onClick={() => setFeedbackEase(option)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          cursor: "pointer",
                          p: 1,
                          borderRadius: 2,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                        }}
                      >
                        {feedbackEase === option ? (
                          <RadioCheckedIcon
                            sx={{
                              fontSize: 20,
                              color: config.appearance.primaryColor,
                            }}
                          />
                        ) : (
                          <RadioUncheckedIcon
                            sx={{ fontSize: 20, color: "text.secondary" }}
                          />
                        )}
                        <Typography variant="body2">{option}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: "auto" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!feedbackRating || !feedbackEase}
                    onClick={() => {
                      if (onSubmitFeedback && feedbackRating && feedbackEase) {
                        onSubmitFeedback(feedbackRating, feedbackEase);
                      }
                      setCurrentView("home");
                    }}
                    sx={{
                      bgcolor: "#1e1e1e",
                      color: "#fff",
                      borderRadius: 3,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: "700",
                      "&:hover": { bgcolor: "#333" },
                      "&.Mui-disabled": { bgcolor: "#ccc" },
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            ) : (
              /* CHAT VIEW */
              <>
                <Box
                  ref={scrollRef}
                  sx={{
                    flex: 1,
                    p: 2.5,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    bgcolor:
                      config.appearance.backgroundColor ||
                      (config.appearance.darkMode ? "#121212" : "#f8f9fa"),
                  }}
                >
                  {messages.map((msg, index) => {
                    const prevMsg = index > 0 ? messages[index - 1] : null;
                    const isNewDay =
                      !prevMsg ||
                      !moment(msg.timestamp).isSame(prevMsg.timestamp, "day");

                    const isAudioOnly =
                      msg.fileUrl &&
                      !msg.text &&
                      (msg.fileType?.startsWith("audio/") ||
                        msg.fileUrl.match(/\.(mp3|wav|ogg|m4a|webm)(\?|$)/i));

                    const isMediaOnly =
                      msg.fileUrl &&
                      !msg.text &&
                      !isAudioOnly &&
                      (msg.fileType?.startsWith("image/") ||
                        msg.fileType?.startsWith("video/") ||
                        msg.fileUrl.match(
                          /\.(jpeg|jpg|gif|png|webp|svg|mp4)(\?|$)/i,
                        ));

                    return (
                      <React.Fragment key={msg.id}>
                        {isNewDay && (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              my: 1,
                              position: "relative",
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                top: "50%",
                                left: 0,
                                right: 0,
                                height: "1px",
                                bgcolor: config.appearance.darkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.05)",
                                zIndex: 1,
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor:
                                  config.appearance.backgroundColor ||
                                  (config.appearance.darkMode
                                    ? "#121212"
                                    : "#f8f9fa"),
                                px: 2,
                                color: "text.secondary",
                                fontWeight: 600,
                                position: "relative",
                                zIndex: 2,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {moment(msg.timestamp).format(
                                "ddd, MMM DD, YYYY",
                              )}
                            </Typography>
                          </Box>
                        )}

                        {/* System Event Divider */}
                        {msg.isSystem ? (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              my: 0.5,
                              px: 1,
                              "&::before": {
                                content: '""',
                                flex: 1,
                                height: "1px",
                                bgcolor: config.appearance.darkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.08)",
                              },
                              "&::after": {
                                content: '""',
                                flex: 1,
                                height: "1px",
                                bgcolor: config.appearance.darkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "20px",
                                bgcolor: config.appearance.darkMode
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.05)",
                                color: config.appearance.darkMode
                                  ? "#aaa"
                                  : "#666",
                                fontStyle: "italic",
                                fontSize: "11px",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                maxWidth: "80%",
                                textAlign: "center",
                                lineHeight: 1.6,
                              }}
                            >
                              {msg.text}
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              alignSelf:
                                msg.sender === "bot"
                                  ? "flex-start"
                                  : "flex-end",
                              maxWidth: "85%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems:
                                msg.sender === "bot"
                                  ? "flex-start"
                                  : "flex-end",
                            }}
                          >
                            <Box
                              sx={{
                                background: isAudioOnly
                                  ? "transparent"
                                  : msg.sender === "bot"
                                  ? "rgba(241, 245, 249, 0.8)" // Premium Incoming Gray
                                  : `linear-gradient(135deg, ${config.appearance.primaryColor} 0%, ${config.appearance.primaryColor}ee 100%)`, // Dynamic Premium Outgoing Purple
                                color:
                                  msg.sender === "bot" ? "#1e293b" : "#ffffff",
                                backdropFilter:
                                  msg.sender === "bot" ? "blur(8px)" : "none",
                                boxSizing: "border-box",
                                p: isMediaOnly ? 0 : isAudioOnly ? 0 : 1.5,
                                borderRadius: isMediaOnly
                                  ? "8px"
                                  : isAudioOnly
                                  ? "24px"
                                  : msg.sender === "bot"
                                  ? "18px 18px 18px 4px"
                                  : "18px 18px 4px 18px",
                                boxShadow: isAudioOnly
                                  ? "none"
                                  : "0 1px 2px rgba(0,0,0,0.05)",
                                opacity: msg.isSending ? 0.7 : 1,
                                transition: "all 0.3s ease",
                              }}
                            >
                              <Box>
                                {msg.fileUrl &&
                                  (() => {
                                    const isImage =
                                      msg.fileType?.startsWith("image/") ||
                                      msg.fileUrl.match(
                                        /\.(jpeg|jpg|gif|png|webp|svg)(\?|$)/i,
                                      );
                                    const isVideo =
                                      msg.fileType?.startsWith("video/") ||
                                      msg.fileUrl.match(
                                        /\.(mp4|webm|ogg)(\?|$)/i,
                                      );
                                    const isAudio =
                                      msg.fileType?.startsWith("audio/") ||
                                      msg.fileUrl.match(
                                        /\.(mp3|wav|ogg|m4a|webm)(\?|$)/i,
                                      );

                                    if (isImage) {
                                      return (
                                        <Box sx={{ mb: msg.text ? 1 : 0 }}>
                                          <img
                                            src={msg.fileUrl}
                                            alt="attachment"
                                            style={{
                                              maxWidth: "100%",
                                              maxHeight: "120px",
                                              borderRadius: "8px",
                                              display: "block",
                                              cursor: "pointer",
                                            }}
                                            onClick={() =>
                                              window.open(
                                                msg.fileUrl!,
                                                "_blank",
                                              )
                                            }
                                          />
                                        </Box>
                                      );
                                    }
                                    if (isAudio) {
                                      return (
                                        <Box
                                          sx={{
                                            mb: msg.text ? 1 : 0,
                                            display: "flex",
                                            justifyContent:
                                              msg.sender === "bot"
                                                ? "flex-start"
                                                : "flex-end",
                                          }}
                                        >
                                          <audio
                                            src={msg.fileUrl}
                                            controls
                                            style={{
                                              width: "220px",
                                              maxWidth: "100%",
                                              display: "block",
                                              height: "40px",
                                              outline: "none",
                                            }}
                                          />
                                        </Box>
                                      );
                                    }
                                    if (isVideo) {
                                      return (
                                        <Box sx={{ mb: msg.text ? 1 : 0 }}>
                                          <video
                                            src={msg.fileUrl}
                                            controls
                                            style={{
                                              maxWidth: "100%",
                                              maxHeight: "150px",
                                              borderRadius: "8px",
                                              display: "block",
                                            }}
                                          />
                                        </Box>
                                      );
                                    }
                                    return (
                                      <Box sx={{ mb: msg.text ? 1 : 0 }}>
                                        <a
                                          href={msg.fileUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "inherit",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          Download File
                                        </a>
                                      </Box>
                                    );
                                  })()}
                                {msg.text && (
                                  <Box
                                    sx={{
                                      fontSize: "0.875rem",
                                      lineHeight: 1.5,
                                      "& p": { m: 0, mb: 1 },
                                      "& p:last-child": { mb: 0 },
                                      "& ul, & ol": {
                                        m: 0,
                                        pl: 2.5,
                                        mb: 1,
                                      },
                                      "& li": {
                                        mb: 0.5,
                                      },
                                      "& li:last-child": {
                                        mb: 0,
                                      },
                                      "& code": {
                                        bgcolor:
                                          msg.sender === "bot"
                                            ? "rgba(0,0,0,0.05)"
                                            : "rgba(255,255,255,0.2)",
                                        p: "2px 4px",
                                        borderRadius: "4px",
                                        fontSize: "0.85em",
                                        fontFamily: "monospace",
                                      },
                                      "& a": {
                                        color:
                                          msg.sender === "bot"
                                            ? config.appearance.primaryColor
                                            : "#fff",
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {msg.text}
                                    </ReactMarkdown>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{ mt: 0.5, opacity: 0.5, px: 0.5 }}
                            >
                              {moment(msg.timestamp).format("HH:mm:ss")}
                            </Typography>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {isTyping && (
                    <Box
                      sx={{
                        alignSelf: "flex-start",
                        p: 1.5,
                        borderRadius: "18px 18px 18px 4px",
                        bgcolor: config.appearance.darkMode
                          ? "#2d2d2d"
                          : "#ffffff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: config.appearance.darkMode
                              ? "#666"
                              : "#aaa",
                            animation: "pulse 1.4s infinite ease-in-out both",
                            animationDelay: `${i * 0.2}s`,
                            "@keyframes pulse": {
                              "0%, 80%, 100%": { transform: "scale(0)" },
                              "40%": { transform: "scale(1.0)" },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  {/* Error Message */}
                  {botError && (
                    <Box
                      sx={{
                        m: 2,
                        mt: 0,
                        p: 1.5,
                        bgcolor: "rgba(211, 47, 47, 0.1)",
                        borderRadius: 1,
                        border: "1px solid rgba(211, 47, 47, 0.3)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#d32f2f",
                          display: "block",
                          textAlign: "center",
                          fontWeight: "500",
                        }}
                      >
                        {botError}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Input Area or Start New Chat Button */}
                <Box
                  sx={{
                    p: 2,
                    borderTop: "1px solid",
                    borderColor: config.appearance.darkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {closedByCustomer ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={onResetChat}
                      disabled={isResetLoading}
                      startIcon={isResetLoading ? null : <RefreshIcon />}
                      sx={{
                        bgcolor: config.appearance.primaryColor,
                        color: "#fff",
                        borderRadius: 3,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: "700",
                        fontSize: "0.95rem",
                        "&:hover": {
                          bgcolor: config.appearance.primaryColor,
                          opacity: 0.9,
                          transform: isResetLoading
                            ? "none"
                            : "translateY(-1px)",
                        },
                        transition: "all 0.2s",
                        boxShadow: `0 4px 12px ${config.appearance.primaryColor}33`,
                        "&.Mui-disabled": {
                          bgcolor: config.appearance.primaryColor,
                          opacity: 0.7,
                          color: "#fff",
                        },
                      }}
                    >
                      {isResetLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Start New Chat"
                      )}
                    </Button>
                  ) : (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      {/* Unified Input Capsule */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          p: "4px 4px 4px 8px",
                          borderRadius: "24px",
                          bgcolor: config.appearance.darkMode
                            ? "#2d2d2d"
                            : "#f0f2f5",
                          border: "1px solid",
                          borderColor: config.appearance.darkMode
                            ? "#444"
                            : "#e5e7eb",
                        }}
                      >
                        <Tooltip title="Attachment" arrow>
                          <IconButton
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={!config.enabled}
                            sx={{ color: "text.secondary" }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <input
                          type="text"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder={
                            config.behavior?.placeholderText ||
                            "Write a message..."
                          }
                          style={{
                            flex: 1,
                            border: "none",
                            background: "transparent",
                            outline: "none",
                            color: config.appearance.darkMode ? "#fff" : "#000",
                            fontSize: "14px",
                            padding: "8px 4px",
                            fontFamily: "inherit",
                          }}
                        />

                        <Tooltip title="Voice to Text (AI)" arrow>
                          <IconButton
                            size="small"
                            onClick={handleToggleRecording}
                            disabled={!config.enabled || isTranscribing}
                            sx={{
                              color: isRecording
                                ? "error.main"
                                : config.appearance.darkMode
                                ? "#aaa"
                                : "#666",
                              "&:hover": { color: "error.main" },
                              transition: "all 0.2s",
                              ...(isRecording && {
                                animation: "pulse 1.5s infinite",
                                "@keyframes pulse": {
                                  "0%": { opacity: 1 },
                                  "50%": { opacity: 0.4 },
                                  "100%": { opacity: 1 },
                                },
                              }),
                            }}
                          >
                            {isTranscribing ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : isRecording ? (
                              <MicActiveIcon fontSize="small" />
                            ) : (
                              <MicIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Emoji" arrow>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <EmojiPicker
                              color={
                                config.appearance.darkMode ? "#aaa" : "#666"
                              }
                              activeColor={config.appearance.primaryColor}
                              onEmojiClick={(emojiData: any) =>
                                setInputValue((prev) => prev + emojiData.native)
                              }
                            />
                          </Box>
                        </Tooltip>

                        <IconButton
                          onClick={() => !isSending && handleSendMessage()}
                          disabled={
                            !config.enabled || !inputValue.trim() || isSending
                          }
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: config.appearance.primaryColor,
                            color: "#fff",
                            "&:hover": {
                              bgcolor: config.appearance.primaryColor,
                              opacity: 0.9,
                            },
                            "&.Mui-disabled": {
                              bgcolor: "action.disabledBackground",
                              color: "action.disabled",
                            },
                          }}
                        >
                          {isSending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SendUpIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}

            {/* Bottom Navigation - Only show on Home view */}
            {currentView === "home" && (
              <Box
                sx={{
                  p: 1.5,
                  borderTop: "1px solid",
                  borderColor: "rgba(0,0,0,0.05)",
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  bgcolor: config.appearance.darkMode ? "#1a1a1a" : "#fff",
                  zIndex: 4,
                }}
              >
                {[
                  { view: "home", icon: <HomeIcon />, label: "Home" },
                  { view: "chat", icon: <ChatIcon />, label: "Chat" },
                ].map((tab) => (
                  <Box
                    key={tab.view}
                    onClick={() => setCurrentView(tab.view as "home" | "chat")}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      color:
                        currentView === tab.view
                          ? config.appearance.primaryColor
                          : "text.disabled",
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                        color:
                          currentView === tab.view
                            ? config.appearance.primaryColor
                            : "text.secondary",
                      },
                      border:
                        currentView === tab.view
                          ? `1px solid ${config.appearance.primaryColor}`
                          : "1px solid rgba(0,0,0,0.1)",
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                      minWidth: "100px",
                    }}
                  >
                    {tab.icon}
                    <Typography
                      variant="caption"
                      fontWeight={currentView === tab.view ? "600" : "400"}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Branding Footer */}
            <Box
              sx={{
                py: 1,
                textAlign: "center",
                bgcolor: config.appearance.darkMode ? "#1a1a1a" : "#fff",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  opacity: 1,
                  fontSize: "12px",
                  fontWeight: "700",
                  color: config.appearance.darkMode
                    ? "rgba(255,255,255,0.7)"
                    : "rgba(0,0,0,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  letterSpacing: "0.5px",
                }}
              >
                Powered by{" "}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    component="img"
                    src={KuikwitLogoOnly}
                    sx={{ height: 16, width: "auto" }}
                  />
                  <span style={{ color: config.appearance.primaryColor }}>
                    Kuikwit
                  </span>
                </Box>
              </Typography>
            </Box>
          </Box>

          {/* Close Confirmation Overlay */}
          {showCloseConfirmation && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100,
                p: 3,
              }}
            >
              <Box
                sx={{
                  bgcolor: config.appearance.darkMode ? "#1e1e1e" : "#fff",
                  borderRadius: 4,
                  p: 3,
                  width: "100%",
                  maxWidth: 300,
                  textAlign: "center",
                  position: "relative",
                  boxShadow: 24,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setShowCloseConfirmation(false)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: "rgba(0,0,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 28, color: "text.primary" }} />
                </Box>

                <Typography variant="h6" fontWeight="600" sx={{ mb: 2, px: 2 }}>
                  Do you really want to close the chat?
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (onCloseChat) onCloseChat();
                    setShowCloseConfirmation(false);
                    setCurrentView("feedback");
                  }}
                  sx={{
                    bgcolor: "#D32F2F",
                    color: "#fff",
                    borderRadius: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: "700",
                    fontSize: "1rem",
                    "&:hover": { bgcolor: "#B71C1C" },
                  }}
                >
                  Close the chat
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Toggle Button / Avatar Button - Hide if standalone */}
      {!isStandalone && (
        <Box
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            bgcolor: config.appearance.primaryColor,
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: 3,
            cursor: "pointer",
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        >
          {isOpen ? (
            <CloseIcon sx={{ fontSize: 32 }} />
          ) : config.appearance.launcherImageUrl ? (
            <Box
              component="img"
              src={config.appearance.launcherImageUrl}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <SmartToyIcon sx={{ fontSize: 32 }} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default WidgetPreview;
